import { manhattanDistance } from '@/utils/GridUtils';
import { clamp } from '@/utils/MathUtils';

interface BarPosition {
  col: number;
  row: number;
}

const MAX_DRINK_DISTANCE = 10;

/**
 * Computes drink accessibility based on manhattan distance to nearest bar.
 * Closer = higher value (1 at bar, 0 beyond MAX_DRINK_DISTANCE).
 */
export class DrinkAccessMap {
  private readonly cols: number;
  private readonly rows: number;
  private coverage: Float32Array;

  constructor(cols: number, rows: number) {
    this.cols = cols;
    this.rows = rows;
    this.coverage = new Float32Array(cols * rows);
  }

  recalculate(bars: BarPosition[]): void {
    this.coverage.fill(0);

    if (bars.length === 0) return;

    for (let row = 0; row < this.rows; row++) {
      for (let col = 0; col < this.cols; col++) {
        // Find minimum manhattan distance to any bar
        let minDist = Infinity;
        for (const bar of bars) {
          const dist = manhattanDistance(col, row, bar.col, bar.row);
          if (dist < minDist) minDist = dist;
        }

        const value = minDist >= MAX_DRINK_DISTANCE
          ? 0
          : 1 - minDist / MAX_DRINK_DISTANCE;

        this.coverage[row * this.cols + col] = clamp(value, 0, 1);
      }
    }
  }

  getCoverage(col: number, row: number): number {
    if (col < 0 || col >= this.cols || row < 0 || row >= this.rows) return 0;
    return this.coverage[row * this.cols + col];
  }
}
